import { DEFAULT_DESKTOP_IFRAME_CAP, isIframeRuntimeWindow } from './thermal-snapshot';
import type { WindowState } from '../types/window';

export type DesktopIframeThermalMode = 'off' | 'observe' | 'enforce';

export const DESKTOP_IFRAME_THERMAL_MODE_ENV_KEY = 'VITE_KHAL_DESKTOP_IFRAME_THERMAL_MODE';
export const DESKTOP_IFRAME_THERMAL_MODE_STORAGE_KEY = 'khal:desktop:iframe-thermal-mode';
export const DESKTOP_IFRAME_CAP_ENV_KEY = 'VITE_KHAL_DESKTOP_IFRAME_CAP';
export const DESKTOP_IFRAME_CAP_STORAGE_KEY = 'khal:desktop:iframe-cap';
export const MIN_DESKTOP_IFRAME_CAP = 1;
export const MAX_DESKTOP_IFRAME_CAP = 8;

export interface IframeThermalPlan {
	windows: WindowState[];
	mode: DesktopIframeThermalMode;
	cap: number;
	changed: boolean;
	iframeWindowCount: number;
	activeIframeCount: number;
	parkedIframeCount: number;
	capExceeded: boolean;
}

function normalizeMode(value: unknown): DesktopIframeThermalMode | null {
	if (typeof value !== 'string') return null;
	const normalized = value.trim().toLowerCase();
	if (normalized === 'off' || normalized === 'false' || normalized === '0') return 'off';
	if (normalized === 'observe' || normalized === 'on' || normalized === 'true' || normalized === '1') return 'observe';
	if (normalized === 'enforce' || normalized === 'strict') return 'enforce';
	return null;
}

export function resolveDesktopIframeThermalMode({
	envValue,
	storedValue,
}: {
	envValue?: unknown;
	storedValue?: unknown;
} = {}): DesktopIframeThermalMode {
	return normalizeMode(storedValue) ?? normalizeMode(envValue) ?? 'observe';
}

function readImportMetaEnv(key: string): unknown {
	return (import.meta as unknown as { env?: Record<string, unknown> }).env?.[key];
}

function readLocalStorage(key: string): string | null {
	try {
		return globalThis.localStorage?.getItem(key) ?? null;
	} catch {
		return null;
	}
}

export function resolveRuntimeDesktopIframeThermalMode(): DesktopIframeThermalMode {
	return resolveDesktopIframeThermalMode({
		envValue: readImportMetaEnv(DESKTOP_IFRAME_THERMAL_MODE_ENV_KEY),
		storedValue: readLocalStorage(DESKTOP_IFRAME_THERMAL_MODE_STORAGE_KEY),
	});
}

export function resolveDesktopIframeCap({
	envValue,
	storedValue,
}: {
	envValue?: unknown;
	storedValue?: unknown;
} = {}): number {
	const raw = storedValue ?? envValue;
	const parsed = typeof raw === 'number' ? raw : Number.parseInt(String(raw ?? ''), 10);
	if (!Number.isFinite(parsed)) return DEFAULT_DESKTOP_IFRAME_CAP;
	return Math.max(MIN_DESKTOP_IFRAME_CAP, Math.min(MAX_DESKTOP_IFRAME_CAP, parsed));
}

export function resolveRuntimeDesktopIframeCap(): number {
	return resolveDesktopIframeCap({
		envValue: readImportMetaEnv(DESKTOP_IFRAME_CAP_ENV_KEY),
		storedValue: readLocalStorage(DESKTOP_IFRAME_CAP_STORAGE_KEY),
	});
}

function isParked(win: WindowState): boolean {
	return win.meta?.framePaused === true || win.meta?.iframeParked === true || win.meta?.parked === true;
}

function withFramePaused(win: WindowState, framePaused: boolean): WindowState {
	if (framePaused ? win.meta?.framePaused === true : win.meta?.framePaused === false) return win;
	return { ...win, meta: { ...win.meta, framePaused } };
}

function livePriority(win: WindowState, preferredActiveWindowId?: string | null): number {
	const preferred = win.id === preferredActiveWindowId ? 1_000_000 : 0;
	const focused = win.focused ? 100_000 : 0;
	return preferred + focused + win.zIndex;
}

export function planIframeThermalState({
	windows,
	mode = 'observe',
	cap = DEFAULT_DESKTOP_IFRAME_CAP,
	preferredActiveWindowId = null,
}: {
	windows: WindowState[];
	mode?: DesktopIframeThermalMode;
	cap?: number;
	preferredActiveWindowId?: string | null;
}): IframeThermalPlan {
	const clampedCap = resolveDesktopIframeCap({ storedValue: cap });
	const iframeWindows = windows.filter(isIframeRuntimeWindow);
	const visibleIframeWindows = iframeWindows.filter((win) => !win.minimized);
	const activeIframeCount = visibleIframeWindows.filter((win) => !isParked(win)).length;
	const parkedIframeCount = iframeWindows.filter(isParked).length;
	const capExceeded = activeIframeCount > clampedCap;

	if (mode !== 'enforce') {
		return {
			windows,
			mode,
			cap: clampedCap,
			changed: false,
			iframeWindowCount: iframeWindows.length,
			activeIframeCount,
			parkedIframeCount,
			capExceeded,
		};
	}

	const keepLive = new Set(
		visibleIframeWindows
			.slice()
			.sort((a, b) => livePriority(b, preferredActiveWindowId) - livePriority(a, preferredActiveWindowId))
			.slice(0, clampedCap)
			.map((win) => win.id)
	);

	let changed = false;
	const nextWindows = windows.map((win) => {
		if (!isIframeRuntimeWindow(win)) return win;
		const shouldPause = win.minimized || !keepLive.has(win.id);
		const next = withFramePaused(win, shouldPause);
		if (next !== win) changed = true;
		return next;
	});

	const nextIframeWindows = nextWindows.filter(isIframeRuntimeWindow);
	const nextVisibleIframeWindows = nextIframeWindows.filter((win) => !win.minimized);
	const nextActiveIframeCount = nextVisibleIframeWindows.filter((win) => !isParked(win)).length;
	const nextParkedIframeCount = nextIframeWindows.filter(isParked).length;

	return {
		windows: nextWindows,
		mode,
		cap: clampedCap,
		changed,
		iframeWindowCount: nextIframeWindows.length,
		activeIframeCount: nextActiveIframeCount,
		parkedIframeCount: nextParkedIframeCount,
		capExceeded,
	};
}

export function planRuntimeIframeThermalState(windows: WindowState[], preferredActiveWindowId?: string | null): IframeThermalPlan {
	return planIframeThermalState({
		windows,
		preferredActiveWindowId,
		mode: resolveRuntimeDesktopIframeThermalMode(),
		cap: resolveRuntimeDesktopIframeCap(),
	});
}
